import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'

/** ══ HỢP ĐỒNG API — HỢP ĐỒNG KHÁCH HÀNG / THẦU PHỤ và BẢNG GIÁ HỢP ĐỒNG ══════════════════════ */

/**
 * ⚠ ĐÚNG MỘT PHÍA. Lược đồ chỉ cho hai trường TỒN TẠI; luật "đúng một" chốt ở `@rs/contract`
 *   (`contractSideError`) để câu từ chối là tiếng Việt, và ở CSDL (`az_contract_one_side`) để
 *   chắc. Khai `.refine` ở đây nữa là bản thứ ba của cùng một luật.
 */
export const CreateContractBody = z.object({
  customerId: blankAsAbsent(Uuid),
  vendorId: blankAsAbsent(Uuid),
  templateId: z.string().optional(),
  proposeNote: z.string().optional(),
  body: z.string().optional(),
})

/**
 * ĐÍNH THẲNG MỘT HỢP ĐỒNG ĐÃ KÝ — đường tắt KHÔNG qua trình ký (bản cũ `QuickContractAttach`,
 * `AzContract.tsx:697`). Mọi ô chi tiết đều TÙY CHỌN: bản cũ tạo ra một dòng trống rồi để người
 * dùng điền dần ngay tại chỗ, nên bắt buộc số/hiệu lực ở đây là chặn đúng thao tác đầu tiên.
 */
export const AttachSignedContractBody = z.object({
  customerId: blankAsAbsent(Uuid),
  vendorId: blankAsAbsent(Uuid),
  contractNo: z.string().optional(),
  validFrom: blankAsAbsent(ISODate),
  validTo: blankAsAbsent(ISODate),
})

export const UpdateContractBody = z.object({
  version: Version,
  proposeNote: z.string().optional(),
  body: z.string().optional(),
})

export const ProposeContractBody = z.object({
  version: Version,
  proposeNote: z.string(),
  assignedApproverId: blankAsAbsent(Uuid.nullable()),
})

export const DecideBody = z.object({
  version: Version,
  approve: z.boolean({ required_error: 'Thiếu `approve`.' }),
  /** Lý do TRẢ VỀ — chỉ có nghĩa khi `approve: false`; vào thẳng dòng lịch sử. */
  reason: z.string().max(500).optional(),
})

export const ContractDetailsBody = z.object({
  version: Version,
  contractNo: z.string(),
  validFrom: ISODate,
  validTo: ISODate,
  creditDays: blankAsAbsent(z.coerce.number().int()),
  value: blankAsAbsent(moneyField('Giá trị hợp đồng')),
})

export const ContractSignedBody = z.object({ version: Version, signDate: ISODate })
export const ContractStepBody = z.object({ version: Version })
export const ContractReceiveBody = z.object({
  version: Version,
  receivedDate: ISODate,
  receivedBy: z.string().optional(),
})
/** Chuyển CHỜ BỔ SUNG — lý do BẮT BUỘC: cờ không nói thiếu gì thì sale không biết đi bổ sung cái gì. */
export const ContractSupplementBody = z.object({
  version: Version,
  reason: z.string().max(500),
})

/* ══ GIA HẠN · PHỤ LỤC · TRAO ĐỔI (`0124`) ═══════════════════════════════════════════════════ */

export const RenewContractBody = z.object({
  version: Version,
  validFrom: ISODate,
  validTo: ISODate,
  note: z.string().max(500).optional(),
})

/**
 * ⚠ KHÔNG có `no`: số phụ lục do MÁY CHỦ đánh. Nhận số từ màn là hai người cùng mở một hợp đồng sẽ
 *   gửi lên cùng một số, và hai phụ lục trùng số là hai tờ giấy không phân biệt được.
 */
export const AppendixBody = z.object({
  version: Version,
  body: z.string().min(1, 'Nội dung phụ lục không được để trống.').max(100_000),
  templateId: blankAsAbsent(Uuid),
  /** Mã báo giá đã chốt mà phụ lục soạn ra từ đó — để trống nếu soạn từ mẫu. */
  sourceQuoteRef: blankAsAbsent(z.string().max(60)),
  /**
   * ⭐ ĐỔ GIÁ VÀO "ĐƠN GIÁ HĐ" từ báo giá này (T41, soát 16/10; bản cũ `AzContract.tsx:1132`). Màn CHỈ gửi khi phụ lục soạn
   * từ báo giá mà thân KHÔNG SỬA — sửa là đã thương lượng lại, không tự đổ. Máy chủ kiểm báo giá đúng mã, đúng khách, đã chốt.
   */
  syncPricesFromQuoteId: blankAsAbsent(Uuid),
})

/** Sửa NGÀY NHẬN bản gốc sau khi đã nhận (L34, bản cũ "Ngày nhận (sửa được)", `AzContract.tsx:900-903`). */
export const ContractReceivedDateBody = z.object({ version: Version, receivedDate: ISODate })

export const UpdateAppendixBody = z.object({
  version: Version,
  body: z.string().min(1, 'Nội dung phụ lục không được để trống.').max(100_000),
})

export const AppendixParams = z.object({ id: Uuid, appendixId: Uuid })

/** 🔗 Link bản Word đã sửa gửi khách — chuỗi rỗng là GỠ link, xem `setShareUrl`. */
export const ContractShareUrlBody = z.object({
  version: Version,
  shareUrl: z.string().max(2000)
    .refine((v) => v.trim() === '' || /^https?:\/\//i.test(v.trim()), 'Link phải bắt đầu bằng http:// hoặc https://'),
})

export const ContractCommentBody = z.object({
  version: Version,
  text: z.string().min(1, 'Nội dung trao đổi không được để trống.').max(4000),
})

const contractPriceFields = {
  customerId: blankAsAbsent(Uuid),
  vendorId: blankAsAbsent(Uuid),
  fromPlace: z.string().optional(),
  toPlace: z.string().optional(),
  contType: z.string().optional(),
  cargo: z.string().optional(),
  vatRate: blankAsAbsent(z.coerce.number()),
  effectiveTo: blankAsAbsent(ISODate),
  contractId: blankAsAbsent(Uuid),
  note: z.string().optional(),
}

export const CreateContractPriceBody = z.object({
  unitPrice: moneyField('Đơn giá'),
  effectiveFrom: ISODate,
  ...contractPriceFields,
})

/** Bản VÁ — xem ghi chú ở `UpdateQuoteBody`: chỉ `version` là bắt buộc. */
export const UpdateContractPriceBody = z.object({
  version: Version,
  unitPrice: blankAsAbsent(moneyField('Đơn giá')),
  effectiveFrom: blankAsAbsent(ISODate),
  active: z.boolean().optional(),
  ...contractPriceFields,
})

export const ContractIdParams = z.object({ id: Uuid })
export const ListContractQuery = PageQuery.extend({
  customerId: blankAsAbsent(Uuid),
  vendorId: blankAsAbsent(Uuid),
  /** Lọc theo PHÍA. Bỏ trống = cả hai — màn duyệt cần nhìn hết. */
  side: z.enum(['customer', 'vendor']).optional(),
})
export const ListContractPriceQuery = PageQuery.extend({
  customerId: blankAsAbsent(Uuid),
  vendorId: blankAsAbsent(Uuid),
})
export type ListContractQueryT = z.infer<typeof ListContractQuery>
export type ListContractPriceQueryT = z.infer<typeof ListContractPriceQuery>
export type AttachSignedContractBodyT = z.infer<typeof AttachSignedContractBody>
